import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, KanbanSquare, Users, Megaphone, Settings, LogOut, Zap, Radar, FileText, Briefcase, UserCircle, Package, Receipt, ShieldCheck, MessageCircle, FileSpreadsheet, DollarSign, Palette, Gift, Link2, BarChart3, Sparkles, Wallet, Mail, Star, X, Download } from "lucide-react";
import { isStandalone, OPEN_INSTALL_EVENT } from "@/lib/installPrompt";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const links = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, testid: "sidebar-overview-link", end: true },
  { to: "/app/coach", label: "AI Business Coach", icon: Sparkles, testid: "sidebar-coach-link" },
  { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "sidebar-pipeline-link" },
  { to: "/app/leads", label: "Leads", icon: Users, testid: "sidebar-leads-link" },
  { to: "/app/customers", label: "Customers", icon: UserCircle, testid: "sidebar-customers-link" },
  { to: "/app/estimates", label: "Estimates", icon: FileText, testid: "sidebar-estimates-link" },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase, testid: "sidebar-jobs-link" },
  { to: "/app/invoices", label: "Invoices", icon: Receipt, testid: "sidebar-invoices-link" },
  { to: "/app/payments", label: "Payments", icon: Wallet, testid: "sidebar-payments-link" },
  { to: "/app/email-inbox", label: "Email inbox", icon: Mail, testid: "sidebar-email-inbox-link" },
  { to: "/app/reviews", label: "Reviews", icon: Star, testid: "sidebar-reviews-link" },
  { to: "/app/financials", label: "Financials", icon: DollarSign, testid: "sidebar-financials-link" },
  { to: "/app/services", label: "Services", icon: Package, testid: "sidebar-services-link" },
  { to: "/app/document-designer", label: "Document Designer", icon: Palette, testid: "sidebar-document-designer-link" },
  { to: "/app/imports", label: "Data Import", icon: FileSpreadsheet, testid: "sidebar-imports-link" },
  { to: "/app/campaigns", label: "Campaigns", icon: Megaphone, testid: "sidebar-campaigns-link" },
  { to: "/app/marketing-dashboard", label: "Marketing Dashboard", icon: BarChart3, testid: "sidebar-marketing-dashboard-link" },
  { to: "/app/automations", label: "Automations", icon: Radar, testid: "sidebar-automations-link" },
  { to: "/app/rewards", label: "Rewards", icon: Gift, testid: "sidebar-rewards-link" },
  { to: "/app/integrations", label: "Integrations", icon: Link2, testid: "sidebar-integrations-link" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "sidebar-settings-link" },
];

// Super-admin-only nav — appended at runtime based on user.is_super_admin.
const superAdminLinks = [
  { to: "/app/access-requests", label: "Access requests", icon: ShieldCheck, testid: "sidebar-access-requests-link" },
  { to: "/app/admin/unmatched-emails", label: "Unmatched emails", icon: Mail, testid: "sidebar-unmatched-emails-link" },
];

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, business, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const visibleLinks = user?.is_super_admin ? [...links, ...superAdminLinks] : links;

  useEffect(() => {
    let active = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/comms/unread/count");
        if (active) setUnread(data?.count || 0);
      } catch { /* non-fatal */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
    {mobileOpen && (
      <div
        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        onClick={onClose}
        data-testid="mobile-menu-backdrop"
      />
    )}
    <aside
      data-testid="app-sidebar"
      onClick={(e) => { if (e.target.closest("a")) onClose(); }}
      className={`sidebar-dark w-64 shrink-0 flex flex-col text-zinc-300 border-r border-zinc-900 fixed inset-y-0 left-0 z-50 overflow-y-auto transition-transform duration-200 ease-out lg:relative lg:translate-x-0 lg:min-h-screen lg:z-auto lg:overflow-visible ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="px-6 pt-6 pb-8">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden order-last ml-auto p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="mobile-menu-close-btn"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-white font-bold text-lg leading-none">LeadForge</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Ops OS</div>
          </div>
        </div>
      </div>

      {!isStandalone() && (
        <button
          type="button"
          onClick={() => { onClose(); window.dispatchEvent(new Event(OPEN_INSTALL_EVENT)); }}
          className="lg:hidden mx-3 -mt-4 mb-4 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-300 border border-zinc-800 hover:text-white hover:bg-zinc-800/50"
          data-testid="sidebar-install-app-btn"
        >
          <Download className="w-4 h-4 text-[#FF5A00]" />
          <span>Install app on this phone</span>
        </button>
      )}
      <nav className="flex-1 px-3 space-y-1">
        {visibleLinks.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative ${
                isActive
                  ? "bg-zinc-800 text-white before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#FF5A00] before:rounded-r"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
            {to === "/app" && unread > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate("/app#unread");
                }}
                className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF5A00] text-white text-[10px] font-mono-accent hover:bg-[#E04F00] transition-colors cursor-pointer"
                data-testid="sidebar-unread-badge"
                title="Jump to unread communications"
              >
                {unread > 99 ? "99+" : unread}
              </button>
            )}
          </NavLink>
        ))}
        {user?.is_super_admin && (
          <>
            <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest text-zinc-600 font-mono-accent">Platform</div>
            <NavLink
              to="/app/superadmin"
              end
              data-testid="sidebar-superadmin-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative ${
                  isActive
                    ? "bg-zinc-800 text-white before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#FF5A00] before:rounded-r"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4"/>
              <span>Businesses</span>
            </NavLink>
            <NavLink
              to="/app/superadmin/tremendous"
              data-testid="sidebar-superadmin-tremendous-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative ${
                  isActive
                    ? "bg-zinc-800 text-white before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#FF5A00] before:rounded-r"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                }`
              }
            >
              <Gift className="w-4 h-4"/>
              <span>Tremendous Ops</span>
            </NavLink>
            <NavLink
              to="/app/superadmin/feedback"
              data-testid="sidebar-superadmin-feedback-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative ${
                  isActive
                    ? "bg-zinc-800 text-white before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#FF5A00] before:rounded-r"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                }`
              }
            >
              <MessageCircle className="w-4 h-4"/>
              <span>Feedback</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="p-3 border-t border-zinc-900">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-white text-sm font-semibold">
            {(user?.full_name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.full_name || user?.email}</div>
            <div className="text-xs text-zinc-500 truncate">{business?.business_name || "—"}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="sidebar-logout-btn"
            className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}
